import { Principal } from '@icp-sdk/core/principal'
import { getPrincipals, importKeypairWallet } from '@plug-core/handlers/walletActions/wallet'
import { logger } from '@plug-core/logger'
import { setDefaultWalletName } from '@plug-core/utils/setDefaultWalletName'
import { useFindTokensProvider } from '@providers/FindTokensProvider'
import { Button, Text, cn } from '@ui'
import { ParsedIdentity, getIdentityFromPem } from '@utils/parsePem'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useDropzone } from 'react-dropzone'
import { useTranslation } from 'react-i18next'
import { HiCheckBadge } from 'react-icons/hi2'
import { useNavigate } from 'react-router-dom'

const WalletImportPrivateKey = () => {
  const [selectedFile, setSelectedFile] = useState<File>()
  const [parsedIdentity, setParsedIdentity] = useState<ParsedIdentity>()
  const [isError, setError] = useState(false)
  const [importLoading, setImportLoading] = useState(false)
  const [existingPrincipals, setExistingPrincipals] = useState<Principal[]>([])

  const navigate = useNavigate()
  const { t } = useTranslation()
  const { findTokens } = useFindTokensProvider()

  const onDrop = useCallback((acceptedFiles: File[]) => {
    if (acceptedFiles.length > 0) {
      setSelectedFile(acceptedFiles[0])
    }
  }, [])

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { 'application/x-x509-ca-cert': ['.pem'] },
  })

  useEffect(() => {
    if (!selectedFile) return
    setParsedIdentity(undefined)

    let fileReader = new FileReader()
    fileReader.readAsText(selectedFile)

    fileReader.onloadend = () => {
      const content = fileReader.result
      try {
        setParsedIdentity(getIdentityFromPem(content))
        setError(false)
      } catch (e) {
        logger.error('Failed to parse identity from pem', { originalError: e })
        setError(true)
      }
    }
  }, [selectedFile])

  useEffect(() => {
    getPrincipals()
      .then(setExistingPrincipals)
      .catch((e) => logger.error('Failed to get principals', { originalError: e }))
  }, [])

  const fileLabelClass = useMemo(() => {
    if (isError) return 'border-danger text-danger'

    return parsedIdentity ? 'border-success text-success' : 'border-hairline-strong text-fg'
  }, [isError, parsedIdentity])

  const onImportClick = useCallback(() => {
    if (!parsedIdentity) return

    setImportLoading(true)
    const keyPair = JSON.stringify(parsedIdentity.identity.toJSON())

    importKeypairWallet(keyPair)
      .then(async (wallet) => {
        await findTokens(wallet.principal)
        await setDefaultWalletName(wallet.principal)
        navigate(-2)
      })
      .catch((e) => logger.error('Failed keypair wallet import', { originalError: e }))
  }, [parsedIdentity])

  const alreadyExists = useMemo(() => {
    if (!parsedIdentity) return false

    return existingPrincipals
      .map((p) => p.toString())
      .includes(parsedIdentity.identity.getPrincipal().toString())
  }, [existingPrincipals, parsedIdentity])

  const textLabel = isDragActive
    ? t('importPrivateKey.dropHere')
    : t('importPrivateKey.dragAndDrop')

  const renderPropertyRow = (title: string, value: string, mono?: boolean) => (
    <div className="flex flex-col gap-2">
      <Text variant="label" tone="fg-2">
        {title}
      </Text>
      <div className="rounded-md border border-solid border-hairline bg-surface px-4 py-2">
        <Text variant={mono ? 'mono' : 'body'} breakAll={mono}>
          {value}
        </Text>
      </div>
    </div>
  )

  const renderIdentityDetails = () => {
    if (!parsedIdentity) return

    return (
      <div className="flex flex-1 flex-col justify-between">
        <div className="flex flex-col gap-4">
          {renderPropertyRow('Principal', parsedIdentity.identity.getPrincipal().toString(), true)}
          {renderPropertyRow('Identity Type', parsedIdentity.type)}
        </div>
        <div className="mt-6 mb-6">
          <Button
            variant="primary"
            onClick={onImportClick}
            loading={importLoading}
            disabled={alreadyExists}
            fullWidth
          >
            {alreadyExists ? t('importPrivateKey.alreadyExists') : t('importPrivateKey.import')}
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className="flex flex-1 flex-col">
      <div
        {...getRootProps({
          className: cn(
            'my-4 flex cursor-pointer flex-col items-center gap-2 rounded-lg border border-dashed bg-surface p-4 transition-colors duration-150 hover:bg-surface-2 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus',
            fileLabelClass
          ),
        })}
      >
        <input {...getInputProps()} />
        <HiCheckBadge size={30} aria-hidden="true" />
        <Text align="center" tone="inherit">
          {selectedFile?.name || textLabel}
        </Text>
      </div>
      {renderIdentityDetails()}
    </div>
  )
}

export default WalletImportPrivateKey
